declare const DOMPurify: { sanitize: (html: string) => string }

import { Component, Inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { INotification, NotificationType } from '../../models/notification.models'

export interface NotificationFormDialogData {
  notification?: INotification
}

@Component({
  selector: 'app-notification-form-dialog',
  templateUrl: './notification-form-dialog.component.html',
  styleUrls: ['./notification-form-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NotificationFormDialogComponent {
  readonly isEditMode: boolean
  readonly typeOptions = [
    { value: NotificationType.ReleaseNotes, label: 'Release Notes' },
    { value: NotificationType.Outage, label: 'Outage' },
    { value: NotificationType.ImportantUpdate, label: 'Important Update' },
  ]

  readonly quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ header: [1, 2, 3, false] }],
      ['link'],
      ['clean'],
    ],
  }

  form: FormGroup
  charCount = 0
  readonly maxChars = 5000

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<NotificationFormDialogComponent>,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: NotificationFormDialogData,
  ) {
    this.isEditMode = !!data?.notification

    this.form = this.fb.group({
      title: [data?.notification?.title ?? '', [Validators.required, Validators.maxLength(255)]],
      type: [data?.notification?.type ?? NotificationType.ReleaseNotes, Validators.required],
      content: [data?.notification?.content ?? '', Validators.required],
      sendNotification: [false],
    })

    this.charCount = this.stripHtml(data?.notification?.content ?? '').length
  }

  onContentChanged(event: { text: string }): void {
    this.charCount = Math.max(0, (event.text?.length ?? 1) - 1)
    this.cdr.markForCheck()
  }

  onCancel(): void {
    this.dialogRef.close(null)
  }

  onSave(): void {
    if (this.form.invalid || this.charCount > this.maxChars) return

    const raw = this.form.getRawValue()
    const content = DOMPurify.sanitize(raw.content)

    this.dialogRef.close({
      title: raw.title.trim(),
      type: raw.type,
      content,
      sendNotification: this.isEditMode ? raw.sendNotification : true,
    })
  }

  private stripHtml(html: string): string {
    return html.replace(/<[^>]*>/g, '')
  }
}
